/**
 * useSchedulingRules Hook
 *
 * Manages scheduling rules for the production planning schedule engine.
 * Rules define hard/soft constraints for nudging biological dates onto
 * the human calendar (avoid weekend harvests, min gaps between phases, etc.)
 *
 * US-PPL-001 Phase 5: Management UI — Scheduling Rules Configuration
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

/**
 * Hook to fetch all scheduling rules
 * @returns {Object} { rules, loading, error, refetch }
 */
export function useSchedulingRules() {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRules = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('scheduling_rules')
        .select('*')
        .order('constraint_type', { ascending: true })
        .order('priority_rank', { ascending: true })

      if (fetchError) throw fetchError
      setRules(data || [])
    } catch (err) {
      console.error('[useSchedulingRules] Fetch error:', err)
      setError(err.message || 'Failed to fetch scheduling rules')
      setRules([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  return { rules, loading, error, refetch: fetchRules }
}

/**
 * Hook providing mutation functions for scheduling rules
 * @returns {Object} { upsertRule, deleteRule, toggleRule, previewRuleChange, loading, error }
 */
export function useSchedulingRuleMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const upsertRule = useCallback(async (ruleData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('upsert_scheduling_rule', {
        p_id: ruleData.id || null,
        p_rule_type: ruleData.rule_type,
        p_rule_name: ruleData.rule_name,
        p_description: ruleData.description || null,
        p_parameters: ruleData.parameters || {},
        p_constraint_type: ruleData.constraint_type || 'soft',
        p_priority_rank: ruleData.priority_rank || 100,
        p_is_active: ruleData.is_active !== undefined ? ruleData.is_active : true
      })

      if (rpcError) throw rpcError

      toast.success(ruleData.id ? 'Rule updated' : 'Rule created')
      return data
    } catch (err) {
      console.error('[useSchedulingRules] Upsert error:', err)
      toast.error('Failed to save rule')
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteRule = useCallback(async (ruleId) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('scheduling_rules')
        .delete()
        .eq('id', ruleId)

      if (deleteError) throw deleteError
      toast.success('Rule deleted')
    } catch (err) {
      console.error('[useSchedulingRules] Delete error:', err)
      toast.error('Failed to delete rule')
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const toggleRule = useCallback(async (ruleId, isActive) => {
    try {
      const { error: updateError } = await supabase
        .from('scheduling_rules')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('id', ruleId)

      if (updateError) throw updateError
      toast.success(isActive ? 'Rule activated' : 'Rule deactivated')
    } catch (err) {
      console.error('[useSchedulingRules] Toggle error:', err)
      toast.error('Failed to toggle rule')
      throw err
    }
  }, [])

  const previewRuleChange = useCallback(async (ruleId, newParams = null, activate = null) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('preview_rule_change', {
        p_rule_id: ruleId,
        p_new_parameters: newParams,
        p_activate: activate
      })

      if (rpcError) throw rpcError
      return data
    } catch (err) {
      console.error('[useSchedulingRules] Preview error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { upsertRule, deleteRule, toggleRule, previewRuleChange, loading, error }
}
